import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Bannerimg from "../../Assets/banner.png";
import Banner from "../../components/UserSideComponents/common/Banner";
import { IoStarSharp } from "react-icons/io5";
import API from "../../Utils/api";
import InquiryModal from "../../components/common/InquiryModal";

// Skeleton for main product
const DetailSkeleton = () => (
  <div className="w-full bg-white shadow-lg rounded-2xl p-6 grid grid-cols-1 md:grid-cols-2 gap-8 animate-pulse">
    <div>
      <div className="w-full h-[350px] bg-gray-200 rounded-xl" />
      <div className="flex gap-3 mt-4 justify-center">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="w-40 h-20 bg-gray-200 rounded-lg" />
        ))}
      </div>
    </div>
    <div className="flex flex-col gap-4">
      <div className="h-10 bg-gray-200 rounded w-3/4" />
      <div className="flex gap-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="w-5 h-5 bg-gray-200 rounded-full" />
        ))}
      </div>
      <div className="h-10 bg-gray-200 rounded w-1/3" />
      <div className="space-y-2">
        <div className="h-3 bg-gray-200 rounded w-full" />
        <div className="h-3 bg-gray-200 rounded w-5/6" />
        <div className="h-3 bg-gray-200 rounded w-4/6" />
      </div>
      <div className="h-4 bg-gray-200 rounded w-1/2" />
      <div className="h-4 bg-gray-200 rounded w-1/2" />
      <div className="h-4 bg-gray-200 rounded w-2/3" />
      <div className="h-12 bg-gray-200 rounded-xl w-40 mt-4" />
    </div>
  </div>
);

// Top product card skeleton
const TopProductSkeleton = () => (
  <div className="bg-white shadow-md rounded-2xl p-4 flex items-center border animate-pulse">
    <div className="w-full h-[150px] bg-gray-200 rounded-xl flex-shrink-0" />
    <div className="flex flex-col w-full gap-3 mt-3 ml-3">
      <div className="h-5 bg-gray-200 rounded w-3/4" />
      <div className="h-3 bg-gray-200 rounded w-2/3" />
      <div className="h-3 bg-gray-200 rounded w-2/3" />
      <div className="h-3 bg-gray-200 rounded w-1/2" />
    </div>
  </div>
);

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [activeImage, setActiveImage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [topLoading, setTopLoading] = useState(true);
  const [error, setError] = useState("");
  const [showInquiry, setShowInquiry] = useState(false);
  const menuRef = React.useRef();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  // Fetch single product
  const fetchProduct = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await API.get(`/product/${id}`);
      const data = res.data.data || res.data;
      setProduct(data);
      setActiveImage(0);
    } catch (err) {
      console.error("Error fetching product:", err);
      setError("Failed to load product details.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch top products
  const fetchTopProducts = async () => {
    setTopLoading(true);
    try {
      const res = await API.get("/product/top");
      const data = Array.isArray(res.data.data) ? res.data.data : [];
      setTopProducts(data);
    } catch (err) {
      console.error("Error fetching top products:", err);
    } finally {
      setTopLoading(false);
    }
  };

  useEffect(() => {
    fetchProduct();
    fetchTopProducts();
  }, [id]);

  return (
    <>
      <Banner heading={"Product Details"} image={Bannerimg} />

      <div className="w-full px-4 sm:px-6 md:px-10 py-4 md:py-8 bg-yellow-50/30">
        {/* Error State */}
        {error && !loading && (
          <div className="flex flex-col items-center justify-center md:py-16 text-center">
            <span className="text-5xl mb-4">😕</span>
            <p className="text-gray-600 font-medium">{error}</p>
            <button
              onClick={fetchProduct}
              className="mt-4 px-6 py-2 bg-[#92AD36] text-white rounded-xl text-sm hover:bg-[#7a9230] transition"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Product Detail */}
        {loading ? (
          <DetailSkeleton />
        ) : product ? (
          <div className="w-full flex flex-col md:flex-row bg-white shadow-lg rounded-2xl p-6 gap-8">
            {/* LEFT - IMAGE */}
            <div className="w-full md:w-[40%] ">
              <img
                src={product.images?.[activeImage]}
                alt={product.name}
                className="w-full h-[350px] object-contain border rounded-xl"
              />
              {/* Thumbnails */}
              {product.images?.length > 1 && (
                <div className="grid grid-cols-4 items-center justify-center gap-3 mt-4">
                  {product.images.map((img, i) => (
                    <img
                      key={i}
                      src={img}
                      alt="sub"
                      onClick={() => setActiveImage(i)}
                      className={`w-40 h-24 object-contain border rounded-lg cursor-pointer hover:scale-105 transition ${
                        activeImage === i ? "border-[#92AD36] border-2" : ""
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* RIGHT - DETAILS */}
            <div className="flex flex-col gap-4">
              <h2 className="text-4xl font-['Times_new_roman'] text-black">
                {product.name}
              </h2>

              {/* Rating */}
              <div className="flex items-center gap-2">
                {[...Array(5)].map((_, i) => (
                  <span
                    key={i}
                    className={`text-lg ${i < product.rating ? "text-yellow-500" : "text-gray-300"}`}
                  >
                    ★
                  </span>
                ))}
                <span className="text-sm text-gray-500">
                  ({product.reviewCount} reviews)
                </span>
              </div>

              <p className="text-gray-600 text-sm leading-relaxed tracking-wide">
                {product.description}
              </p>

              <div className="flex flex-col gap-4 w-full">
                {product.dosage && (
                  <div className="flex gap-3">
                    <span className="font-semibold text-sm">Dosage :</span>
                    <p className="text-gray-600 text-xs mt-1">
                      {product.dosage}
                    </p>
                  </div>
                )}
                {product.packSizes?.length > 0 && (
                  <div className="flex gap-3">
                    <span className="font-semibold text-sm">Pack Size :</span>
                    <p className="text-gray-600 text-xs mt-1">
                      {product.packSizes.join(", ")}
                    </p>
                  </div>
                )}
                {product.recommendedCrops?.length > 0 && (
                  <div className="flex gap-3 w-full">
                    <span className="font-semibold text-sm">
                      Recommended Crops :
                    </span>
                    <p className="text-gray-600 text-xs mt-1">
                      {product.recommendedCrops.join(", ")}
                    </p>
                  </div>
                )}
                {/* Stock */}
                <div className="flex gap-3">
                  <span className="font-semibold text-sm">Availability :</span>
                  <span
                    className={`text-xs mt-1 font-medium ${product.stock > 0 ? "text-[#407B00]" : "text-red-500"}`}
                  >
                    {product.stock > 0
                      ? `In Stock (${product.stock})`
                      : "Out of Stock"}
                  </span>
                </div>
              </div>

              <button
                onClick={() => setShowInquiry(true)}
                className="mt-4 bg-[#92AD36] text-white py-3 px-7 rounded-xl hover:bg-white hover:text-[#92AD36] border border-[#92AD36] transition duration-300"
              >
                Enquiry Now
              </button>
            </div>
          </div>
        ) : null}

        {/* Top Products Section */}
        <div className="mt-14 w-full bg-yellow-50/30">
          <h3 className="text-2xl md:text-4xl font-serif font-bold text-[#000000] mb-10">
            Top Sale Products
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
            {topLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TopProductSkeleton key={i} />
              ))
            ) : topProducts.length === 0 ? (
              <p className="text-gray-400 text-sm col-span-3">
                No top products available.
              </p>
            ) : (
              topProducts.map((item) => (
                <div
                  key={item._id}
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                    navigate(`/products/${item._id}`);
                  }}
                  className="bg-white relative shadow-md rounded-2xl p-4 flex items-center border hover:border-[#3A7138] mb-5 transition hover:scale-105 duration-300 hover:shadow-xl hover:backdrop-blur-sm cursor-pointer"
                >
                  <img
                    src={item.images?.[0]}
                    alt={item.name}
                    className="w-full h-[150px] object-contain"
                  />
                  <span
                    className={`absolute top-1 right-1 px-3 py-1 text-xs rounded-2xl text-white ${item.stock > 0 ? "bg-[#407B00]" : "bg-red-500"}`}
                  >
                    {item.stock > 0 ? "In Stock" : "Out of Stock"}
                  </span>
                  <div className="flex flex-col w-full gap-3 mt-3">
                    <h3 className="text-xl font-semibold text-[#425340] mt-2">
                      {item.name}
                    </h3>
                    <p className="text-sm mt-2 text-[#425340]">
                      <span>Dosage: </span>
                      {item.dosage || "—"}
                    </p>
                    <p className="text-sm mt-2 text-[#425340]">
                      <span>Pack Size: </span>
                      {item.packSizes?.join(", ") || "—"}
                    </p>

                    <div className="flex text-yellow-400">
                      {[...Array(5)].map((_, i) => (
                        <IoStarSharp
                          key={i}
                          size={20}
                          className={
                            i < item.rating
                              ? "text-yellow-400"
                              : "text-gray-200"
                          }
                        />
                      ))}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      {showInquiry && <InquiryModal onClose={() => setShowInquiry(false)} />}
    </>
  );
};

export default ProductDetails;
